// apps/web/src/features/clients/components/ClientVersionSnapshotView.tsx
//
// Read-only render of one historical snapshot: the client fields, its contacts
// and its legal entities WITH the requisites text recorded at that moment
// (AC-11) — that text is usually the reason someone opens a version at all.
import { useTranslation } from 'react-i18next';
import {
  formatPhone,
  phoneSchema,
  type ClientVersionSnapshot,
  type ClientVersionLegalEntitySnapshot,
} from '@magimp/shared';
import { Chip, StatusBadge } from '../../../ui/index.js';
import { CopyTextButton } from './CopyTextButton.js';
import { ExpandableText } from './ExpandableText.js';

const CARD_STYLE = {
  padding: 'var(--s-3) var(--s-4)',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--r-2)',
  minWidth: 0,
};

export function ClientVersionSnapshotView({ snapshot }: { snapshot: ClientVersionSnapshot }) {
  const { t } = useTranslation(['clients', 'common']);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
      <Row label={t('clients:fields.name')} value={snapshot.name} />
      <Row label={t('clients:fields.comment')} value={snapshot.comment ?? '—'} />

      <h3 style={{ fontSize: 'var(--fz-13)', fontWeight: 600, margin: '8px 0 0' }}>
        {t('clients:sections.contacts')}
      </h3>
      {snapshot.contacts.length === 0 && (
        <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:emptyContacts')}</p>
      )}
      {snapshot.contacts.map((c) => (
        <div
          key={c.id}
          style={{ ...CARD_STYLE, background: c.archivedAt ? 'var(--bg-2)' : 'var(--bg-1)' }}
        >
          <div
            style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <span style={{ fontWeight: 500 }}>{c.fullName}</span>
            {c.isPrimary && <Chip tone="accent">{t('clients:badges.primary')}</Chip>}
            {c.archivedAt && <StatusBadge status="archived" size="sm" />}
          </div>
          <div
            style={{
              fontSize: 'var(--fz-12)',
              color: 'var(--fg-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {phoneSchema.safeParse(c.phone).success ? formatPhone(c.phone) : c.phone}
          </div>
        </div>
      ))}

      <h3 style={{ fontSize: 'var(--fz-13)', fontWeight: 600, margin: '8px 0 0' }}>
        {t('clients:sections.legalEntities')}
      </h3>
      {snapshot.legalEntities.length === 0 && (
        <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:emptyLegal')}</p>
      )}
      {snapshot.legalEntities.map((e) => (
        <LegalSnapshotCard key={e.id} entity={e} />
      ))}
    </div>
  );
}

function LegalSnapshotCard({ entity }: { entity: ClientVersionLegalEntitySnapshot }) {
  const { t } = useTranslation(['clients', 'common']);
  return (
    <div
      data-testid="client-version-legal"
      style={{ ...CARD_STYLE, background: entity.archivedAt ? 'var(--bg-2)' : 'var(--bg-1)' }}
    >
      <div style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{entity.name}</span>
        <Chip icon="building-2">{t(`clients:legalType.${entity.type}`)}</Chip>
        {entity.archivedAt && <StatusBadge status="archived" size="sm" />}
      </div>
      <div style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
        {t('clients:fields.inn')}{' '}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{entity.inn}</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--s-2)',
          marginTop: 'var(--s-2)',
        }}
      >
        <ExpandableText
          text={entity.requisites}
          expandLabel={t('clients:actions.expandRequisitesOf', { name: entity.name })}
        />
        <div>
          <CopyTextButton
            text={entity.requisites}
            label={t('clients:actions.copyRequisitesOf', {
              name: entity.name,
              entity: entity.name,
            })}
          >
            {t('clients:actions.copyRequisites')}
          </CopyTextButton>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'baseline', minWidth: 0 }}>
      <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)', minWidth: 100 }}>{label}</span>
      <span style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{value}</span>
    </div>
  );
}
